import React, { ReactNode, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ConfirmationModalContext } from '../context'
import { ConfirmationModal } from '../widgets/Modals/ConfirmationModal'

export const ConfirmationModalProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [title, setTitle] = useState('')
    const [contentText, setContentText] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [confirmActionLabel, setConfirmActionLabel] = useState('')
    const [onConfirm, setOnConfirm] = useState<() => void | Promise<unknown>>(
        () => () => {}
    )
    const [awaitConfirm, setAwaitConfirm] = useState(false)
    const [isConfirming, setIsConfirming] = useState(false)
    const [keepSearchParams, setKeepSearchParams] = useState(false)
    const [modalChildren, setModalChildren] = useState<ReactNode | null>(null)
    const [, setSearchParams] = useSearchParams()

    const showConfirmationModal = ({
        title,
        contentText,
        confirmActionLabel,
        onConfirm,
        isLoading,
        children,
        awaitConfirm,
        keepSearchParams,
    }: {
        title: string
        contentText?: string
        confirmActionLabel: string
        isLoading: boolean
        onConfirm: () => void | Promise<unknown>
        children?: ReactNode
        awaitConfirm?: boolean
        keepSearchParams?: boolean
    }) => {
        setTitle(title)
        setContentText(contentText || '')
        setOnConfirm(() => onConfirm)
        setIsOpen(true)
        setConfirmActionLabel(confirmActionLabel)
        setIsLoading(isLoading)
        setModalChildren(children || null)
        setAwaitConfirm(!!awaitConfirm)
        setKeepSearchParams(!!keepSearchParams)
    }

    const closeConfirmationModal = () => {
        setIsOpen(false)
        setModalChildren(null)
        if (!keepSearchParams) setSearchParams()
    }

    const handleConfirm = async () => {
        if (!awaitConfirm) {
            onConfirm()
            closeConfirmationModal()
            return
        }

        // Keep the dialog open with a disabled, loading confirm button until
        // the action settles, so it can't be submitted twice.
        if (isConfirming) return
        setIsConfirming(true)
        try {
            await onConfirm()
        } finally {
            setIsConfirming(false)
            closeConfirmationModal()
        }
    }

    return (
        <ConfirmationModalContext.Provider
            value={{
                showConfirmationModal,
                closeConfirmationModal,
                isConfirmationModalOpen: isOpen,
            }}
        >
            {children}

            {isOpen && (
                <ConfirmationModal
                    title={title}
                    isOpen={isOpen}
                    contentText={contentText}
                    confirmActionLabel={confirmActionLabel}
                    isLoading={isLoading || isConfirming}
                    onConfirm={handleConfirm}
                    onCloseModal={() => {
                        if (!isConfirming) closeConfirmationModal()
                    }}
                >
                    {modalChildren}
                </ConfirmationModal>
            )}
        </ConfirmationModalContext.Provider>
    )
}
